import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../../lib/apiBase";
import {
  Activity, Users, Eye, MousePointer, Send, Target, Clock, TrendingDown,
  ArrowRight, FileText, Zap, Globe, Mail, ChevronRight, Trash2,
} from "lucide-react";
import {
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area,
  BarChart, Bar, Cell,
} from "recharts";

const API = `${BACKEND_URL}/api`;

const formatDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

const fmt = (n) => (n || 0).toLocaleString("de-CH");

// Format a duration in seconds as `mm:ss` (or `Xs` if under a minute) — easier
// to read than raw seconds, especially for longer sessions.
const fmtDuration = (sec) => {
  const s = Math.round(Number(sec) || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${String(rem).padStart(2, "0")}s`;
};

const Kpi = ({ icon, label, value, sub, accent = "text-slate-900" }) => (
  <div className="al-card p-4">
    <div className="flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-widest font-semibold">
      {icon} {label}
    </div>
    <div className={`mt-1 font-display font-bold text-2xl ${accent}`}>{value}</div>
    {sub != null && <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>}
  </div>
);

// Map CTA ids → human friendly labels
const CTA_LABELS = {
  hero_primary: "Hero · Primary",
  hero_secondary: "Hero · Sekundär",
  nav_desktop: "Navbar (Desktop)",
  nav_mobile: "Navbar (Mobile)",
  footer_contact: "Footer · Kontakt",
  unknown: "Unbekannt",
};

export default function AnalyticsView() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;

  const fetchAll = async () => {
    if (!token) return navigate("/admin/login");
    setLoading(true);
    try {
      const [s, sum] = await Promise.all([
        axios.get(`${API}/admin/analytics/daily?days=${days}`, { headers: { "x-admin-token": token } }),
        axios.get(`${API}/admin/analytics/summary?days=${days}`, { headers: { "x-admin-token": token } }),
      ]);
      setStats(s.data);
      setSummary(sum.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("alphalane_admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetAnalytics = async () => {
    const ok = window.confirm(
      "⚠️ ALLE Analytics-Events unwiderruflich löschen?\n\n" +
      "Damit werden Besucher, Pageviews, CTA-Klicks, Verweildauer & Funnel " +
      "auf null zurückgesetzt. Tracking läuft sofort neu an — bestehende " +
      "Sessions werden weitergezählt. Diese Aktion lässt sich nicht rückgängig machen."
    );
    if (!ok) return;
    setResetting(true);
    try {
      const res = await axios.post(
        `${API}/admin/analytics/reset?confirm=true`,
        {},
        { headers: { "x-admin-token": token } }
      );
      toast.success(`${res.data.deleted.toLocaleString("de-CH")} Events gelöscht. Tracking läuft frisch.`);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset fehlgeschlagen.");
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [days]);

  const chartData = stats.map((r) => ({ ...r, label: formatDay(r.date) }));

  return (
    <div data-testid="admin-analytics-view">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <span className="al-tag">Analytics</span>
          <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl">
            Besucher & <span className="al-grad-text">Conversions.</span>
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-xl">
            Privacy-freundliches On-Site-Tracking — keine Cookies, keine Drittanbieter. Daten bleiben auf eurem Server.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`analytics-range-${d}`}
              className={`px-3 py-1.5 rounded-full border text-sm font-display font-semibold transition-colors ${
                days === d ? "al-grad-bg text-white border-transparent" : "border-[var(--al-line)] text-slate-600 hover:text-slate-900"
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={resetAnalytics}
            disabled={resetting || loading}
            data-testid="analytics-reset"
            title="Alle Tracking-Daten unwiderruflich löschen"
            className="ml-1 px-3 py-1.5 rounded-full border border-rose-200 bg-white text-sm font-display font-semibold text-rose-600 hover:bg-rose-50 hover:border-rose-300 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {resetting ? "Lösche…" : "Reset"}
          </button>
        </div>
      </div>

      {loading || !summary ? (
        <div className="al-card p-12 text-center text-slate-500">Lade Statistiken…</div>
      ) : (
        <>
          {/* Headline KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-3">
            <Kpi
              icon={<Users className="h-4 w-4" />}
              label="Besucher"
              value={fmt(summary.totals.sessions)}
              sub={`${fmt(summary.totals.pageviews)} Pageviews`}
              accent="al-grad-text"
            />
            <Kpi
              icon={<Eye className="h-4 w-4" />}
              label="Ø Seiten / Besuch"
              value={summary.totals.avg_pages_per_session}
              sub={`${fmtDuration(summary.totals.avg_seconds)} Verweildauer`}
            />
            <Kpi
              icon={<TrendingDown className="h-4 w-4" />}
              label="Bounce Rate"
              value={`${summary.totals.bounce_rate}%`}
              sub="Sessions mit nur 1 PV"
              accent={summary.totals.bounce_rate > 70 ? "text-rose-600" : "text-slate-900"}
            />
            <Kpi
              icon={<MousePointer className="h-4 w-4" />}
              label="CTA-Klicks"
              value={fmt(summary.totals.cta_clicks)}
              sub={`${summary.funnel?.[2]?.rate_total || 0}% der Besucher`}
            />
            <Kpi
              icon={<Send className="h-4 w-4" />}
              label="Anfrage-Konversion"
              value={`${summary.funnel?.[4]?.rate_total || 0}%`}
              sub={`${fmt(summary.totals.contact_requests)} Anfragen gesamt`}
              accent="text-emerald-600"
            />
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <Kpi
              icon={<Mail className="h-4 w-4" />}
              label="Leads (E-Mails)"
              value={fmt(summary.totals.leads_captured)}
              sub={`${summary.totals.visitor_to_lead_rate}% der Besucher`}
              accent="text-[var(--al-purple)]"
            />
            <Kpi
              icon={<Target className="h-4 w-4" />}
              label="Lead-Magnet CR"
              value={`${summary.lead_magnet.rate}%`}
              sub={`${summary.lead_magnet.captured} / ${summary.lead_magnet.shown} (${summary.lead_magnet.shown_rate ?? 0}% sehen Popup)`}
            />
            <Kpi
              icon={<Activity className="h-4 w-4" />}
              label="Track Record gesehen"
              value={fmt(summary.funnel?.[1]?.count || 0)}
              sub={`${summary.funnel?.[1]?.rate_total || 0}% der Besucher`}
            />
            <Kpi
              icon={<Clock className="h-4 w-4" />}
              label="Ø Verweildauer"
              value={fmtDuration(summary.totals.avg_seconds)}
              sub={`Über alle Sessions (${summary.totals.avg_seconds}s)`}
            />
          </div>

          {/* Visitor trend chart */}
          <div className="al-card p-6 sm:p-8 mb-8">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Tägliche Besucher</div>
                <div className="mt-1 font-display font-semibold text-lg">Sessions pro Tag (letzte {days} Tage)</div>
              </div>
            </div>
            <div className="mt-6 h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="visGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="visStroke" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#4A8FE7" />
                      <stop offset="100%" stopColor="#A855F7" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9E5F4" vertical={false} />
                  <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "white", border: "1px solid #E9E5F4", borderRadius: 12, fontSize: 13 }} />
                  <Area type="monotone" dataKey="sessions" stroke="url(#visStroke)" strokeWidth={2.5} fill="url(#visGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Funnel + Lead-magnet card */}
          <div className="grid lg:grid-cols-3 gap-5 mb-8">
            <div className="al-card p-6 sm:p-8 lg:col-span-2" data-testid="analytics-funnel">
              <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Conversion-Funnel</div>
                  <div className="mt-1 font-display font-semibold text-lg">Vom Besucher zur Anfrage</div>
                </div>
                <div className="text-xs text-slate-500">% = Anteil aller Besucher</div>
              </div>
              <FunnelView funnel={summary.funnel} />
            </div>

            <div className="al-card p-6 sm:p-8">
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Lead-Magnet</div>
              <div className="mt-1 font-display font-semibold text-lg">Insider-Report Popup</div>
              <div className="mt-6 space-y-4">
                <Stat
                  label="Popup angezeigt"
                  value={summary.lead_magnet.shown}
                  sub={`${summary.lead_magnet.shown_rate ?? 0}% aller Besucher`}
                />
                <Stat
                  label="E-Mail eingetragen"
                  value={summary.lead_magnet.captured}
                  accent="text-[var(--al-purple)]"
                />
                <div className="pt-3 border-t border-[var(--al-line)]">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-slate-600">Conversion Rate</span>
                    <span className="font-display font-bold text-2xl al-grad-text">{summary.lead_magnet.rate}%</span>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                    Branchenschnitt für Lead-Magnet-Popups liegt bei 2–5%. Über 5% gilt als sehr stark.
                  </p>
                </div>
                <div className="pt-3 border-t border-[var(--al-line)]">
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    <strong className="text-slate-700">Hinweis:</strong> Trigger: 15s Verweildauer, 30% Scroll oder
                    Exit-Intent — was zuerst kommt. Nach Dismiss erscheint das Popup beim nächsten Besuch
                    nach <em>1h Cooldown</em> erneut. Eingetragene Leads werden nie wieder gefragt.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Top pages, Top CTAs, Scroll distribution */}
          <div className="grid lg:grid-cols-3 gap-5 mb-8">
            <div className="al-card p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500">
                <FileText className="h-3.5 w-3.5" /> Top Seiten
              </div>
              <h3 className="mt-1 font-display font-semibold text-lg">Meistbesucht</h3>
              <ul className="mt-4 divide-y divide-[var(--al-line)]">
                {summary.top_pages.length === 0 && <li className="py-4 text-sm text-slate-500">Noch keine Daten.</li>}
                {summary.top_pages.map((p, i) => (
                  <li key={p.path} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-slate-400 font-mono-tab">#{i + 1}</div>
                      <div className="text-sm font-mono-tab text-slate-700 truncate">{p.path}</div>
                    </div>
                    <span className="text-sm font-display font-bold tabular-nums">{fmt(p.views)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="al-card p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500">
                <Zap className="h-3.5 w-3.5" /> Top CTAs
              </div>
              <h3 className="mt-1 font-display font-semibold text-lg">Welche Buttons konvertieren?</h3>
              <ul className="mt-4 divide-y divide-[var(--al-line)]">
                {summary.top_ctas.length === 0 && <li className="py-4 text-sm text-slate-500">Noch keine CTA-Klicks.</li>}
                {summary.top_ctas.map((c, i) => (
                  <li key={c.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-slate-400 font-mono-tab">#{i + 1}</div>
                      <div className="text-sm font-display font-semibold truncate">{CTA_LABELS[c.id] || c.id}</div>
                    </div>
                    <span className="text-sm font-display font-bold tabular-nums">{fmt(c.clicks)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="al-card p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500">
                <ChevronRight className="h-3.5 w-3.5" /> Scroll-Tiefe
              </div>
              <h3 className="mt-1 font-display font-semibold text-lg">Wie weit lesen Besucher?</h3>
              <div className="mt-5 space-y-3.5">
                {summary.scroll_distribution.map((s) => (
                  <div key={s.milestone}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-slate-600 font-mono-tab">{s.milestone}%</span>
                      <span className="font-display font-semibold">{s.rate}% <span className="text-xs text-slate-400 font-normal">({s.sessions})</span></span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full al-grad-bg rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, s.rate)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
                Wenige Besucher scrollen bis 100% — versuche wichtige CTAs vor 50% zu platzieren.
              </p>
            </div>
          </div>

          {/* Weekday + Hour breakdowns + Referrers */}
          <div className="grid lg:grid-cols-3 gap-5 mb-8">
            <div className="al-card p-6">
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Wochentag</div>
              <h3 className="mt-1 font-display font-semibold text-lg">Wann kommen Besucher?</h3>
              <div className="mt-5 h-[170px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.weekday} margin={{ top: 6, right: 6, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9E5F4" vertical={false} />
                    <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "white", border: "1px solid #E9E5F4", borderRadius: 12, fontSize: 13 }} />
                    <Bar dataKey="sessions" radius={[6, 6, 0, 0]}>
                      {summary.weekday.map((d, i) => (
                        <Cell key={d.day ?? i} fill="#A855F7" fillOpacity={0.85} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="al-card p-6 lg:col-span-2">
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Tageszeit</div>
              <h3 className="mt-1 font-display font-semibold text-lg">Sessions nach Stunde (MEZ)</h3>
              <div className="mt-5 h-[170px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.hours} margin={{ top: 6, right: 6, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9E5F4" vertical={false} />
                    <XAxis dataKey="hour" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "white", border: "1px solid #E9E5F4", borderRadius: 12, fontSize: 13 }} />
                    <Bar dataKey="sessions" radius={[4, 4, 0, 0]}>
                      {summary.hours.map((d, i) => (
                        <Cell key={d.hour ?? i} fill="#4A8FE7" fillOpacity={0.85} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Referrers */}
          {summary.top_referrers.length > 0 && (
            <div className="al-card p-6 mb-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500">
                <Globe className="h-3.5 w-3.5" /> Referrer
              </div>
              <h3 className="mt-1 font-display font-semibold text-lg">Wo kommen Besucher her?</h3>
              <ul className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {summary.top_referrers.map((r) => (
                  <li key={r.host} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                    <span className="text-sm text-slate-700 truncate font-mono-tab">{r.host}</span>
                    <span className="text-sm font-display font-bold tabular-nums">{fmt(r.count)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Daily detail table */}
          <div className="al-card overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--al-line)]">
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Detailansicht</div>
              <h3 className="mt-1 font-display font-semibold text-lg">Tägliche Werte</h3>
            </div>
            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[var(--al-line)] bg-[var(--al-bg)] text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              <div className="col-span-3">Tag</div>
              <div className="col-span-2 text-right">Besucher</div>
              <div className="col-span-2 text-right">Pageviews</div>
              <div className="col-span-2 text-right">Track Record</div>
              <div className="col-span-2 text-right">Leads</div>
              <div className="col-span-1 text-right">Anfragen</div>
            </div>
            {[...stats].reverse().map((r) => (
              <div key={r.date} className="grid grid-cols-12 gap-3 px-5 py-3 items-center border-b border-[var(--al-line)] last:border-b-0 text-sm">
                <div className="col-span-3 font-display font-semibold">{formatDay(r.date)}</div>
                <div className="col-span-2 text-right font-mono-tab">{r.sessions}</div>
                <div className="col-span-2 text-right font-mono-tab text-slate-600">{r.pageviews}</div>
                <div className="col-span-2 text-right font-mono-tab text-slate-600">{r.track_record_opened}</div>
                <div className="col-span-2 text-right font-mono-tab text-[var(--al-purple)] font-semibold">{r.leads_captured}</div>
                <div className="col-span-1 text-right font-mono-tab text-emerald-600 font-semibold">{r.form_submits}</div>
              </div>
            ))}
            {stats.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">Noch keine Daten.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, sub, accent = "text-slate-900" }) => (
  <div className="flex items-baseline justify-between gap-3">
    <div className="min-w-0">
      <div className="text-sm text-slate-600">{label}</div>
      {sub != null && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
    <span className={`font-display font-bold text-xl ${accent}`}>{fmt(value)}</span>
  </div>
);

const FunnelView = ({ funnel }) => {
  const max = Math.max(...funnel.map((f) => f.count || 0), 1);
  return (
    <ol className="space-y-2.5">
      {funnel.map((step, idx) => {
        const pct = step.count ? Math.max(6, Math.round((step.count / max) * 100)) : 0;
        const isFirst = idx === 0;
        const isLast = idx === funnel.length - 1;
        const drop = !isFirst && funnel[idx - 1].count
          ? Math.round((1 - step.count / funnel[idx - 1].count) * 100)
          : 0;
        return (
          <li key={step.key}>
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <div className="flex items-center gap-2">
                <span className={`text-[11px] font-mono-tab font-bold rounded-md px-1.5 py-0.5 ${isLast ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {idx + 1}
                </span>
                <span className="text-sm font-display font-semibold text-slate-900">{step.label}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-display font-bold tabular-nums">{fmt(step.count)}</span>
                <span className="text-slate-400">·</span>
                <span className="al-grad-text font-display font-bold">{step.rate_total}%</span>
              </div>
            </div>
            <div className="h-7 rounded-md bg-slate-50 overflow-hidden flex items-center">
              <div
                className={`h-full ${isLast ? "bg-emerald-500" : "al-grad-bg"} transition-all duration-700 flex items-center`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {!isFirst && drop > 0 && (
              <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                <ArrowRight className="h-3 w-3" />
                <span>−{drop}% Drop-off vom vorherigen Schritt</span>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};
